import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import "katex/dist/katex.min.css";
import LogoIcon from "../img/agent-logo.png";
import "../index.css";
import Latex from "react-latex-next";
import Lottie from "lottie-react";
import loadingSpinner from "../img/loading-spinner1.gif";
import introAnimation from "../img/intro.json";
import InputButton from "../img/input_btn.png";
import HamburgerButton from "../img/hamburger_btn.png";
import InputActivateButton from "../img/input_btn_activated.png";
import MicButton from "../img/mic_img_bold.png";
import MicActivateButton from "../img/mic_img_bold_white.png";
import StudyPlan from "./StudyPlan";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";

const Chat = ({
  messages,
  setMessages,
  nickname,
  setNickname,
  isAuthenticated,
  setIsAuthenticated,
  showStartMenu,
  setShowStartMenu,
  ws,
  handleLogout,
}) => {
  const navigate = useNavigate();
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [password, setPassword] = useState("");
  const [showIntro, setShowIntro] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentTypingMessageId, setCurrentTypingMessageId] = useState(null);
  const [volume, setVolume] = useState(1.0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentAgent, setCurrentAgent] = useState("고등수학 에이전트");
  const [currentView, setCurrentView] = useState("chat");
  const [agentStatus, setAgentStatus] = useState("");

  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = new SpeechRecognition();
  recognition.lang = "ko-KR";
  recognition.continuous = false;
  recognition.interimResults = false;

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // 메시지 추가 시 호출
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 로컬 스토리지에서 메시지 불러오기
  useEffect(() => {
    const savedMessages = localStorage.getItem("chatMessages");
    if (savedMessages && isAuthenticated) {
      setMessages(JSON.parse(savedMessages));
      // 저장된 메시지가 있으면 시작 메뉴 숨기기
      setShowStartMenu(false);
    }
  }, [isAuthenticated]);

  // 초기 그리팅 메시지 설정
  useEffect(() => {
    if (isAuthenticated && messages.length === 0) {
      const greeting = getGreeting();
      const initialMessage = {
        id: Date.now(),
        text: `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">${nickname} 학생, ${greeting}</h3> <h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">원하는 메뉴를 선택해 주세요!</h3>`,
        isUser: false,
      };
      setMessages([initialMessage]);
      setShowStartMenu(true);
      localStorage.setItem("chatMessages", JSON.stringify([initialMessage]));
    }
  }, [isAuthenticated, nickname, messages.length, setMessages]);

  // 메시지가 변경될 때마다 로컬 스토리지에 저장
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem("chatMessages", JSON.stringify(messages));
    }
  }, [messages]);

  // ws.onmessage 이벤트 핸들러 추가
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      const response = JSON.parse(event.data);
      console.log("Received message:", response);

      if (response.event === "agent_status") {
        const statusData = JSON.parse(response.data);
        if (!currentTypingMessageId) {
          try {
            const decodedMessage = decodeURIComponent(
              JSON.parse('"' + statusData.message.replace(/\"/g, '\\"') + '"')
            );
            setAgentStatus(decodedMessage);
          } catch (error) {
            console.error("메시지 디코딩 실패:", error);
            setAgentStatus(statusData.message);
          }
        }
      } else if (response.event === "result") {
        setIsLoading(false);
        const resultData = JSON.parse(response.data);
        const aiMessageId = Date.now() + 1;
        setCurrentTypingMessageId(aiMessageId);

        setAgentStatus("");

        let imageUrl = null;
        if (resultData.fileData) {
          try {
            const decodedData = decodeURIComponent(
              escape(window.atob(resultData.fileData))
            );
            imageUrl = `data:${resultData.mediaType};base64,${btoa(
              decodedData
            )}`;
          } catch (error) {
            imageUrl = `data:${resultData.mediaType};base64,${resultData.fileData}`;
          }
        }

        const aiMessage = {
          id: aiMessageId,
          text: "",
          isUser: false,
          imageUrl: imageUrl,
        };

        setMessages((prev) => [...prev, aiMessage]);

        // 타이핑 효과 구현
        let currentText = "";
        const typeText = async () => {
          const fullText = resultData.botMessage;
          for (let i = 0; i < fullText.length; i++) {
            if (!document.hidden) {
              await new Promise((resolve) => setTimeout(resolve, 20));
              currentText += fullText[i];
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiMessageId ? { ...msg, text: currentText } : msg
                )
              );
            } else {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiMessageId ? { ...msg, text: fullText } : msg
                )
              );
              break;
            }
          }
          setCurrentTypingMessageId(null);
        };

        typeText();
        setCurrentAgent(resultData.agentType || "default");
      }
    };

    ws.addEventListener("message", handleMessage);

    return () => {
      ws.removeEventListener("message", handleMessage);
    };
  }, [ws, currentTypingMessageId]);

  const getGreeting = () => {
    const hour = new Date().getHours();

    if (hour >= 22 || hour < 6) {
      return "조용히 집중하기 좋은 시간이에요. 서책 에이전트가 함께 할게요.🌙";
    } else if (hour < 12) {
      return "공부하기 좋은 아침이네요. 오늘 하루도 좋은 하루가 될 거예요.☀️";
    } else if (hour < 18) {
      return "서책 에이전트와 오후 시간도 알차게 보내봐요.💪";
    } else {
      return "저녁 공부도 힘차게 해볼까요? 서책 에이전트가 끝까지 응원할게요.😃";
    }
  };

  const showGreetingWithTypingEffect = async () => {
    const messageId = Date.now();
    setMessages([
      {
        id: messageId,
        text: "",
        isUser: false,
      },
    ]);

    const greeting = getGreeting();
    const fullText = `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">${nickname} 학생, ${greeting}</h3> <h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">원하는 메뉴를 선택해 주세요!</h3>`;
    let currentText = "";

    for (let i = 0; i < fullText.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, 20));
      currentText += fullText[i];
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, text: currentText } : msg
        )
      );
    }
    return new Promise((resolve) => setTimeout(resolve, 500));
  };

  const handleButtonClick = (topic) => {
    setShowStartMenu(false);
    localStorage.setItem("showStartMenu", "false");

    const initialMessages = {
      사용법: "사용법 알려줘",
      "힌트 찾기": "힌트를 찾아줘",
      "개념 이해": "개념 이해를 하고싶어",
      "문제 해설": "문제 해설해줘",
    };

    const aiResponses = {
      사용법: `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">라이브데이터 AI 사용법에 대해 알려드리겠습니다. 어떤 부분이 궁금하신가요?</h3>

<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">1. 문제 입력 방법</h3> <h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">2. 힌트 요청 방법</h3> <h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">3. 답안 제출 방법</h3>`,
      "힌트 찾기": `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">문제 해결을 위한 힌트를 찾아드릴게요. 몇 번 문제에 대한 힌트가 필요하신가요?</h3>`,
      "개념 이해": `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">개념 학습을 도와드리겠습니다. 어떤 개념에 대해 궁금하신가요?</h3>

<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">예시:</h3>
<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">* 함수</h3>
<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">* 배열</h3>
<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">* 반복문</h3>`,
      "문제 해설": `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">문제 해설을 도와드리겠습니다. 몇 번 문제에 대해 설명해드릴까요?</h3>`,
    };

    if (initialMessages[topic]) {
      const userMessage = {
        id: Date.now(),
        text: initialMessages[topic],
        isUser: true,
      };

      const aiMessage = {
        id: Date.now() + 1,
        text: aiResponses[topic],
        isUser: false,
      };

      // Hansu
      setMessages([
        {
          id: Date.now() - 1,
          text: `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">${nickname} 학생, ${getGreeting()}</h3> <h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">원하는 메뉴를 선택해 주세요!</h3>`,
          isUser: false,
        },
        userMessage,
        aiMessage,
      ]);
    }
  };

  const formatMessagesForGPT = (messages) => {
    return messages.map((msg) => ({
      role: msg.isUser ? "user" : "assistant",
      content: msg.text,
    }));
  };

  // handleSendMessage 수정
  const handleSendMessage = async () => {
    if (inputMessage.trim() === "" || isLoading) return;

    setShowStartMenu(false);
    localStorage.setItem("showStartMenu", "false");
    setIsLoading(true);

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      isUser: true,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");

    try {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        throw new Error("서버와의 연결이 끊어졌습니다.");
      }

      const messageData = {
        messages: formatMessagesForGPT([...messages, userMessage]),
      };

      ws.send(JSON.stringify(messageData));
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          text: "서버와의 연결이 끊어졌습니다. 페이지를 새로고침하거나 잠시 후 다시 시도해주세요.",
          isUser: false,
        },
      ]);
      setIsLoading(false);
    }
  };

  // 에이전트 타입에 따른 로딩 메시지 반환
  const getLoadingMessage = (agentType) => {
    const messages = {
      default: "질문을 분석중이예요...",
      generate_plot: "질문을 분석중이예요...",
      study_plan: "질문을 분석중이예요...",
      calculate: "질문을 분석중이예요...",
      web_search: "질문을 분석중이예요...",
    };
    return messages[agentType] || messages.default;
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const handleLogin = () => {
    if (password === "123456" && nickname.trim() !== "") {
      setIsAuthenticated(true);
      setShowIntro(true);
      // 로그인 후 메시지 초기화
      setMessages([]);
      setTimeout(() => {
        setShowIntro(false);
      }, 2000);
    } else if (nickname.trim() === "") {
      alert("닉네임을 입력해주세요.");
    } else {
      alert("비밀번호가 틀렸습니다.");
    }
  };

  const handleTitleClick = () => {
    if (isAuthenticated && messages.length <= 1) {
      // 초기 메시지만 있을 때만 시작 메뉴 표시
      setShowStartMenu(true);
    }
  };

  const MessageContent = ({ text, messageId, imageUrl }) => {
    // Latex 수식을 처리하기 위한 함수
    const processLatex = (text) => {
      return text
        .replace(/\\\[(.*?)\\\]/g, (_, match) => {
          return `$$ {\\displaystyle ${match}} $$`;
        })
        .replace(/\\\((.*?)\\\)/g, (_, match) => `$ ${match} $`);
    };

    // blob: 접두사 제거
    // const processImageUrl = (url) => {
    //   return url ? url.replace(/^blob:/, "") : url;
    // };

    return (
      <div
        style={{
          width: "100%",
          "& .katex-display": {
            overflow: "hidden",
            maxWidth: "100%",
          },
          "& .katex": {
            maxWidth: "100%",
            display: "block",
          },
        }}
      >
        {/* 이미지가 있으면 표시 - blob: 접두사 제거 */}
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Generated content"
            className="max-w-full h-auto rounded-lg mb-2"
            style={{ maxHeight: "200px", objectFit: "contain" }}
          />
        )}

        {/* 기존 텍스트 표시 로직 */}
        {text.includes("<h3") ? (
          <div dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          <>
            <h3
              style={{
                fontFamily: "Pretendard",
                fontSize: "14px",
                fontWeight: "700",
                lineHeight: "28.8px",
                letterSpacing: "-0.001em",
                textAlign: "left",
                textUnderlinePosition: "from-font",
                textDecorationSkipInk: "none",
              }}
            >
              <Latex>{processLatex(text.split("\n")[0])}</Latex>
            </h3>
            {text.split("\n").slice(1).join("\n") && (
              <Latex>
                {processLatex(text.split("\n").slice(1).join("\n"))}
              </Latex>
            )}
          </>
        )}
      </div>
    );
  };

  const startListening = () => {
    const recognition = new (window.SpeechRecognition ||
      window.webkitSpeechRecognition)();
    recognition.lang = "ko-KR";
    recognition.interimResults = true;
    recognition.continuous = true;

    let silenceTimer;
    const SILENCE_DURATION = 1800; // 1.8초 동안 음성이 없으면 종료

    recognition.onresult = (event) => {
      // 음성이 감지될 때마다 타이머 리셋
      clearTimeout(silenceTimer);
      silenceTimer = setTimeout(() => {
        recognition.stop();
      }, SILENCE_DURATION);

      const transcript = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join("");

      setInputMessage(transcript);
    };

    recognition.onstart = () => {
      setIsListening(true);
      // 초기 타이머 설정
      silenceTimer = setTimeout(() => {
        recognition.stop();
      }, SILENCE_DURATION);
    };

    recognition.onend = () => {
      setIsListening(false);
      clearTimeout(silenceTimer);
    };

    recognition.start();
  };

  recognition.onerror = (event) => {
    console.error("음성 인식 오류:", event.error);
    setIsListening(false);
  };

  const renderMessage = (message) => {
    // 메시지 텍스트를 처리하는 함수
    const processText = (text) => {
      const lines = text.split("\n");
      let isFirstLine = true;

      return lines.map((line, index) => {
        // 첫 번째 줄이고 **가 나오기 전까지의 텍스트 처리
        if (isFirstLine) {
          isFirstLine = false;
          const firstBoldIndex = line.indexOf("**");
          if (firstBoldIndex === -1) {
            // **가 없는 경우 전체 라인을 h3로 처리
            return (
              <h3
                key={index}
                style={{
                  fontFamily: "Pretendard",
                  fontSize: "14px",
                  fontWeight: "700",
                  lineHeight: "28.8px",
                  letterSpacing: "-0.001em",
                  textAlign: "left",
                  textUnderlinePosition: "from-font",
                  textDecorationSkipInk: "none",
                }}
              >
                {line}
              </h3>
            );
          } else if (firstBoldIndex > 0) {
            // **가 있는 경우 그 전까지의 텍스트만 h3로 처리
            const firstPart = line.substring(0, firstBoldIndex);
            const remainingPart = line.substring(firstBoldIndex);
            return (
              <div key={index}>
                <h3
                  style={{
                    fontFamily: "Pretendard",
                    fontSize: "14px",
                    fontWeight: "700",
                    lineHeight: "28.8px",
                    letterSpacing: "-0.001em",
                    textAlign: "left",
                    textUnderlinePosition: "from-font",
                    textDecorationSkipInk: "none",
                  }}
                >
                  {firstPart}
                </h3>
                {processTextPart(remainingPart)}
              </div>
            );
          }
        }

        // 기존 텍스트 처리 로직
        if (line.startsWith("###")) {
          return (
            <h3 key={index} className="text-lg font-bold mt-4 mb-2">
              {line.replace("###", "").trim()}
            </h3>
          );
        }

        // ** 처리
        if (line.includes("**")) {
          return processTextPart(line, index);
        }

        // 일반 텍스트 처리
        return (
          <p key={index} className={index === 0 ? "" : "mt-2"}>
            {line}
          </p>
        );
      });
    };

    // 텍스트 파트 처리를 위한 헬퍼 함수
    const processTextPart = (text, index) => {
      const parts = text.split(/(\*\*.*?\*\*)/);
      return (
        <p key={index} className={index === 0 ? "" : "mt-4 mb-4"}>
          {parts.map((part, i) => {
            if (part.startsWith("**") && part.endsWith("**")) {
              return <strong key={i}>{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
        </p>
      );
    };

    return (
      <div
        className={`flex ${
          message.role === "user" ? "justify-end" : "justify-start"
        }`}
      >
        <div
          className={`max-w-[80%] rounded-lg p-3 ${
            message.role === "user"
              ? "bg-blue-500 text-white"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {processText(message.content)}
        </div>
      </div>
    );
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  // 엔터키 이벤트 핸들러를 별도 함수로 분리
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !isLoading) {
      e.preventDefault(); // 기본 엔터 동작 방지
      handleSendMessage();
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const resetChat = () => {
    const greeting = getGreeting();
    const initialMessage = {
      id: Date.now(),
      text: `<h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">${nickname} 학생, ${greeting}</h3> <h3 style="font-family: Pretendard; font-size: 14px; font-weight: 700; line-height: 28.8px; letter-spacing: -0.001em; text-align: left; text-underline-position: from-font; text-decoration-skip-ink: none;">원하는 메뉴를 선택해 주세요!</h3>`,
      isUser: false,
    };

    // localStorage 초기화
    localStorage.removeItem("chatMessages");

    // 메시지 상태 초기화
    setMessages([initialMessage]);

    // 시작 메뉴 표시
    setShowStartMenu(true);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center">
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        currentAgent={currentAgent}
        nickname={nickname}
        handleLogout={handleLogout}
      />

      <div className="bg-white p-2 sm:p-4 rounded-lg shadow-md h-[100dvh] w-full max-w-sm sm:max-w-md flex flex-col">
        <div className="flex flex-col h-full">
          <div className="flex flex-row items-center justify-between px-2">
            <div className="flex items-center">
              <img
                src={HamburgerButton}
                alt="메뉴"
                className="w-4 h-4 cursor-pointer"
                onClick={toggleSidebar}
              />
            </div>
            <div className="flex-1">
              <div className="flex flex-col items-center relative">
                <h1
                  className="text-center text-lg sm:text-xl font-bold cursor-pointer mb-1"
                  onClick={handleTitleClick}
                >
                  Book Agent
                </h1>
                <div className="flex items-center gap-2">
                  <div
                    className="text-center text-xs sm:text-sm pb-1 sm:pb-2 border-b"
                    style={{ color: "#86878C" }}
                  >
                    문제집 또는 수학관련 질문 외에는 답변을 할 수 없습니다.
                  </div>
                  <button
                    onClick={resetChat}
                    className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors duration-200 flex items-center gap-1 absolute right-[-30px]"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="w-4"></div>
          </div>

          {currentView === "chat" ? (
            <>
              {!isAuthenticated ? (
                <div className="flex flex-col items-center justify-center gap-4">
                  <input
                    type="text"
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="닉네임을 입력하세요"
                    className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    onKeyPress={(e) => e.key === "Enter" && handleLogin()}
                    placeholder="비밀번호를 입력하세요"
                    className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={handleLogin}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    로그인
                  </button>
                </div>
              ) : showIntro ? (
                <div className="flex-1 flex items-center justify-center">
                  <Lottie
                    animationData={introAnimation}
                    style={{ width: 150 }}
                    loop={true}
                  />
                </div>
              ) : (
                <>
                  {showStartMenu ? (
                    <div className="flex-1 overflow-y-auto">
                      <div className="space-y-4 py-2">
                        {messages.map((message) => (
                          <div
                            key={message.id}
                            className={`flex ${
                              message.isUser ? "justify-end" : "justify-start"
                            }`}
                          >
                            {!message.isUser && (
                              <div className="flex">
                                <img
                                  src={LogoIcon}
                                  alt="Bot Profile"
                                  className="w-8 h-8 rounded-full mr-2"
                                />
                                <div className="max-w-[80%] rounded-lg p-1 break-words bg-white text-gray-800">
                                  <MessageContent text={message.text} />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                        <div className="flex justify-end">
                          <div className="flex flex-wrap gap-[8px] justify-end">
                            <button
                              onClick={() => handleButtonClick("사용법")}
                              className="flex items-center justify-center whitespace-nowrap shadow-custom pt-[8px] pr-[16px] pb-[8px] pl-[16px] bg-white border-2 border-IntroButtonBorderColor rounded-[100px] hover:bg-gray-50 transition duration-1000 ease-in-out font-normal text-AIMessageTextColor"
                            >
                              사용법 알려줘
                            </button>
                            <button
                              onClick={() => handleButtonClick("힌트 찾기")}
                              className="flex items-center justify-center whitespace-nowrap shadow-custom pt-[8px] pr-[16px] pb-[8px] pl-[16px] bg-white border-2 border-IntroButtonBorderColor rounded-[100px] hover:bg-gray-50 transition duration-1000 ease-in-out font-normal text-AIMessageTextColor"
                            >
                              힌트를 찾아줘
                            </button>
                            <button
                              onClick={() => handleButtonClick("개념 이해")}
                              className="flex items-center justify-center whitespace-nowrap shadow-custom pt-[8px] pr-[16px] pb-[8px] pl-[16px] bg-white border-2 border-IntroButtonBorderColor rounded-[100px] hover:bg-gray-50 transition duration-1000 ease-in-out font-normal text-AIMessageTextColor"
                            >
                              개념 이해를 하고싶어
                            </button>
                            <button
                              onClick={() => handleButtonClick("문제 해설")}
                              className="flex items-center justify-center whitespace-nowrap shadow-custom pt-[8px] pr-[16px] pb-[8px] pl-[16px] bg-white border-2 border-IntroButtonBorderColor rounded-[100px] hover:bg-gray-50 transition duration-1000 ease-in-out font-normal text-AIMessageTextColor"
                            >
                              문제 해설해줘
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col h-full">
                      <div className="flex-1 overflow-y-auto max-h-[calc(100dvh-140px)]">
                        <div className="space-y-4 py-2">
                          {messages.map((message) => (
                            <div
                              key={message.id}
                              className={`flex ${
                                message.isUser ? "justify-end" : "justify-start"
                              }`}
                            >
                              {!message.isUser && (
                                <div className="flex animate-fadeIn">
                                  <img
                                    src={LogoIcon}
                                    alt="Bot Profile"
                                    className="w-8 h-8 rounded-full mr-2"
                                  />
                                  <div
                                    className={`max-w-[80%] rounded-lg p-1 break-words ${
                                      message.isUser
                                        ? "bg-blue-500 text-white font-ui text-[var(--FontsizeM)] font-bold leading-[var(--FontLineheightM)] tracking-[0.015em] text-left [text-underline-position:from-font] [text-decoration-skip-ink:none]"
                                        : "bg-white text-gray-800 pt-0 pl-1 pb-0 font-pretendard text-[14px] font-normal leading-[25.2px] tracking-[-0.001em] text-left [text-underline-position:from-font] [text-decoration-skip-ink:none]"
                                    }`}
                                  >
                                    <MessageContent
                                      text={message.text}
                                      messageId={message.id}
                                      imageUrl={message.imageUrl}
                                    />
                                  </div>
                                </div>
                              )}
                              {message.isUser && (
                                <div
                                  className={`shadow-custom max-w-[80%] rounded-[100px] pt-[8px] pr-[16px] pb-[8px] pl-[16px] break-words bg-customPurple text-white font-bold tracking-[0.015em]`}
                                >
                                  <MessageContent text={message.text} />
                                </div>
                              )}
                            </div>
                          ))}
                          {isLoading && !currentTypingMessageId && (
                            <div className="flex justify-start animate-fadeIn">
                              <img
                                src={LogoIcon}
                                alt="Bot Profile"
                                className="w-8 h-8 rounded-full mr-2"
                              />
                              <div className="flex items-center">
                                <div className="flex flex-col">
                                  <span className="text-sm text-gray-600 mb-1">
                                    {agentStatus ||
                                      getLoadingMessage(currentAgent)}
                                  </span>
                                  <img
                                    src={loadingSpinner}
                                    alt="Loading..."
                                    style={{ width: 42, height: 42 }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          <div ref={messagesEndRef} />
                        </div>
                      </div>
                      <div className="border pt-[8px] pr-[12px] pb-[8px] pl-[12px] bg-InputBarColor rounded-[12px]">
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={inputMessage}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyPress}
                            placeholder="질문을 입력해 보세요."
                            className="flex-1 rounded-lg focus:outline-none focus:ring-0 bg-InputBarColor"
                            disabled={isLoading}
                          />
                          <button
                            onClick={startListening}
                            className={`px-1 rounded-full ${
                              isListening
                                ? "bg-red-400 w-8 h-8"
                                : "bg-gray-100 w-10 h-9"
                            } flex items-center justify-center transition-all duration-200`}
                            disabled={isLoading}
                          >
                            <img
                              src={isListening ? MicActivateButton : MicButton}
                              alt="음성 인식"
                              className={`transition-transform duration-100 ${
                                isListening ? "w-6 h-6" : "w-8 h-8"
                              } ${isListening ? "animate-pulse" : ""}`}
                              style={{
                                transform: isListening
                                  ? `scale(${volume})`
                                  : "scale(1)",
                              }}
                            />
                          </button>
                          <button
                            onClick={handleSendMessage}
                            className={`p-0 text-white rounded-full`}
                            disabled={isLoading}
                          >
                            <img
                              src={
                                inputMessage.trim()
                                  ? InputActivateButton
                                  : InputButton
                              }
                              alt="전송 버튼"
                              className="w-8 h-8 transition-transform duration-200 ease-in-out transform hover:scale-95 active:scale-90"
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <StudyPlan
              nickname={nickname}
              onBack={() => setCurrentView("chat")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;
